import { faArrowLeft, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'motion/react';
import { useState, useMemo, useRef, useEffect } from 'react';

import { BottomSlidePanel } from '@components/BottomSlidePanel';
import { GreeksModal } from '@components/GreeksModal';
import { OptionSelectorModal } from '@components/OptionSelectorModal';
import { useAccount } from '@hooks/useAccount';
import { useNavigation, type AppPage } from '@hooks/useNavigation';
import { useOptionChart } from '@hooks/useOptionChart';
import { orderService } from '@services/orderService';
import { getCurrentTime } from '@services/timeshiftService';
import { brandColors } from '@utils/color-utils';

import { TradePanel } from '@screens/market/TradePanel';

import { OptionPriceChart } from './options/OptionPriceChart';

interface OptionChartScreenProps {
  onNavigate: (page: AppPage) => void;
}

export function OptionChartScreen({ onNavigate }: OptionChartScreenProps) {
  const { selectedOption, clearSelectedOption, selectOption } = useNavigation();
  const {
    symbol,
    priceHistory,
    currentPrice,
    spotPrice,
    bidPrice,
    askPrice,
    highPrice,
    lowPrice,
    isLoading,
    isReady,
    userOwnsOption,
    userQuantity,
  } = useOptionChart(selectedOption);
  const { positions } = useAccount();

  const [showBuyPanel, setShowBuyPanel] = useState(false);
  const [showSellPanel, setShowSellPanel] = useState(false);
  const [showOptionSelector, setShowOptionSelector] = useState(false);
  const [showGreeksModal, setShowGreeksModal] = useState(false);
  const [hoveredTimestamp, setHoveredTimestamp] = useState<number | null>(null);
  const [verticalLineX, setVerticalLineX] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState(0);
  const chartsContainerRef = useRef<HTMLDivElement>(null);
  
  // Track previous prices to determine direction
  const prevOptionPriceRef = useRef<number | null>(null);
  const prevSpotPriceRef = useRef<number | null>(null);
  const [optionPriceDirection, setOptionPriceDirection] = useState<'up' | 'down'>('up');
  const [underlyingPriceDirection, setUnderlyingPriceDirection] = useState<'up' | 'down'>('up');
  
  // Countdown timer for option expiry
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    if (!selectedOption) {
      return;
    }
    
    const updateTimeLeft = () => {
      const now = getCurrentTime();
      const timeToExpiryMs = Math.max(0, selectedOption.expiryTimestamp - now);
      const totalSeconds = Math.floor(timeToExpiryMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      setTimeLeft({ hours, minutes, seconds });
    };
    
    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);
    
    return () => clearInterval(interval);
  }, [selectedOption]);
  
  const formatTimeLeft = () => {
    const h = timeLeft.hours.toString().padStart(2, '0');
    const m = timeLeft.minutes.toString().padStart(2, '0');
    const s = timeLeft.seconds.toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };
  
  // Update option price direction when currentPrice changes
  useEffect(() => {
    if (currentPrice === null) {
      return;
    }
    
    if (prevOptionPriceRef.current === null) {
      prevOptionPriceRef.current = currentPrice;
      return;
    }
    
    if (currentPrice !== prevOptionPriceRef.current) {
      const direction = currentPrice > prevOptionPriceRef.current ? 'up' : 'down';
      setOptionPriceDirection(direction);
      prevOptionPriceRef.current = currentPrice;
    }
  }, [currentPrice]);
  
  // Update underlying price direction when spotPrice changes
  useEffect(() => {
    if (spotPrice === null) {
      return;
    }
    
    if (prevSpotPriceRef.current === null) {
      prevSpotPriceRef.current = spotPrice;
      return;
    }
    
    if (spotPrice !== prevSpotPriceRef.current) {
      const direction = spotPrice > prevSpotPriceRef.current ? 'up' : 'down';
      setUnderlyingPriceDirection(direction);
      prevSpotPriceRef.current = spotPrice;
    }
  }, [spotPrice]);

  const handleBack = () => {
    clearSelectedOption();
    onNavigate('optionshome');
  };

  // Redirect back to options list if there's insufficient data after loading
  useEffect(() => {
    if (!selectedOption) {
      return;
    }

    // If we're not loading and not ready after a delay, redirect back
    const timeoutId = setTimeout(() => {
      if (!isLoading && !isReady && priceHistory.length === 0) {
        console.warn('Insufficient data for option chart, redirecting to options list');
        clearSelectedOption();
        onNavigate('optionshome');
      }
    }, 5000); // Wait 5 seconds before redirecting

    return () => clearTimeout(timeoutId);
  }, [selectedOption, isLoading, isReady, priceHistory.length, clearSelectedOption, onNavigate]);

  const handleViewMarket = () => {
    if (!selectedOption) {
      return;
    }
    const marketPages: Record<string, AppPage> = {
      BTC: 'btcmarket',
      ETH: 'ethmarket',
      SOL: 'solmarket',
      XRP: 'xrpmarket',
    };
    onNavigate(marketPages[selectedOption.asset] ?? 'ethmarket');
  };

  const handleBuy = (
    amount: number,
    orderType: 'MARKET' | 'LIMIT',
    limitPrice?: number,
    premium?: number,
  ) => {
    if (!selectedOption) {
      return;
    }

    orderService.createOrder({
      asset: selectedOption.asset,
      type: selectedOption.type,
      strikePrice: selectedOption.strikePrice,
      quantity: amount,
      orderType,
      side: 'BUY',
      expiryTimestamp: selectedOption.expiryTimestamp,
      limitPrice,
    });

    setShowBuyPanel(false);
  };

  const handleSell = (
    amount: number,
    orderType: 'MARKET' | 'LIMIT',
    limitPrice?: number,
    premium?: number,
  ) => {
    if (!selectedOption || !bidPrice) {
      return;
    }

    // Find matching positions for this option
    const matchingPositions = positions.filter(
      pos =>
        pos.asset === selectedOption.asset &&
        pos.strikePrice === selectedOption.strikePrice &&
        pos.type === selectedOption.type &&
        pos.expiryTimestamp === selectedOption.expiryTimestamp,
    );

    if (matchingPositions.length === 0) {
      console.warn('[OptionChartScreen] No matching positions found for sell order');
      return;
    }

    // Sort positions by purchase timestamp (oldest first - FIFO)
    const sortedPositions = [...matchingPositions].sort(
      (a, b) => a.purchaseTimestamp - b.purchaseTimestamp,
    );

    // Create sell orders for positions, matching the requested quantity
    let remainingQuantity = Math.min(amount, userQuantity);
    
    for (const position of sortedPositions) {
      if (remainingQuantity <= 0) {
        break;
      }

      // Sell up to the position's quantity, or the remaining requested quantity
      const sellQuantity = Math.min(position.quantity, remainingQuantity);

    orderService.createOrder({
      asset: selectedOption.asset,
      type: selectedOption.type,
      strikePrice: selectedOption.strikePrice,
        quantity: sellQuantity,
        orderType,
      side: 'SELL',
      expiryTimestamp: selectedOption.expiryTimestamp,
        limitPrice,
        positionId: position.id,
    });

      remainingQuantity -= sellQuantity;
    }

    setShowSellPanel(false);
  };

  const formatPrice = (price: number | null): string => {
    if (price === null) {
      return '-';
    }
    if (price >= 1) {
      return `$${price.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `$${price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })}`;
  };


  const isCall = selectedOption?.type === 'CALL';

  // Find prices at hovered timestamp
  const hoveredPrices = useMemo(() => {
    if (!hoveredTimestamp || priceHistory.length === 0) {
      return null;
    }

    // Find the closest data point to the hovered timestamp
    let closest = priceHistory[0];
    let minDiff = Math.abs(closest.timestamp - hoveredTimestamp);

    for (const point of priceHistory) {
      const diff = Math.abs(point.timestamp - hoveredTimestamp);
      if (diff < minDiff) {
        minDiff = diff;
        closest = point;
      }
    }

    return {
      optionPrice: closest.price,
      underlyingPrice: closest.spotPrice,
    };
  }, [hoveredTimestamp, priceHistory]);

  // Use hovered prices if available, otherwise use current prices
  const displayOptionPrice = hoveredPrices?.optionPrice ?? currentPrice;
  const displayUnderlyingPrice = hoveredPrices?.underlyingPrice ?? spotPrice;

  return (
    <div
      className="bg-brand-dark h-full w-full flex flex-col"
      style={{ backgroundColor: 'var(--brand-dark)' }}
    >
      {/* Header */}
      <div className="relative flex items-center justify-between px-4 py-3 border-b border-white/10">
        <motion.button
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          onClick={handleBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span className="text-sm">Back</span>
        </motion.button>

        <motion.button
          className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 text-white font-semibold hover:text-white/80 transition-colors"
          onClick={() => setShowOptionSelector(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {selectedOption
            ? `${selectedOption.asset} ${isCall ? 'ABOVE' : 'BELOW'} $${selectedOption.strikePrice.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}`
            : symbol}
        </motion.button>

        <div className="flex items-center gap-2">
          {selectedOption && (
            <motion.button
              className="text-white/70 hover:text-white text-sm transition-colors underline-offset-4 hover:underline"
              onClick={() => setShowGreeksModal(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Greeks
            </motion.button>
          )}

          {!selectedOption && (
        <motion.button
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 text-white/70 text-xs hover:bg-white/20 transition-colors"
          onClick={handleViewMarket}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FontAwesomeIcon icon={faChartLine} className="text-xs" />
          <span>Market</span>
        </motion.button>
          )}
        </div>
      </div>

      {/* Price Info */}
      <div className="px-4 py-3 flex items-start justify-between">
        <div>
          <div className="text-white/40 text-xs mb-1">Option Price</div>
          <div
            className={`text-lg font-bold ${
              optionPriceDirection === 'up' ? 'text-price-up' : 'text-price-down'
            }`}
          >
            {formatPrice(displayOptionPrice)}
          </div>
        </div>
        {selectedOption && (
        <div className="text-right">
            <div className="text-white/40 text-xs mb-1">Expires In</div>
            <div className="text-white font-semibold text-lg">{formatTimeLeft()}</div>
          </div>
        )}
      </div>


      {/* Charts */}
      <div
        ref={chartsContainerRef}
        className="flex-1 min-h-0 flex flex-col relative"
      >
        {/* Vertical hover line spanning both charts */}
        {verticalLineX !== null && (
          <div
            className="absolute top-0 bottom-0 w-px bg-white/50 pointer-events-none z-10"
            style={{ left: `${verticalLineX}px` }}
          />
        )}

        {/* Option Price Chart */}
        <div className="flex-1 min-h-0 relative">
        {isLoading && !isReady ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-white/40 text-sm">Loading chart...</div>
          </div>
        ) : (
          <OptionPriceChart
            priceHistory={priceHistory}
              priceDirection={optionPriceDirection}
              showTimeRange={false}
              onHover={setHoveredTimestamp}
              hoveredTimestamp={hoveredTimestamp}
              onXPositionChange={setVerticalLineX}
              zoomLevel={zoomLevel}
              panOffset={panOffset}
              onZoomChange={setZoomLevel}
              onPanChange={setPanOffset}
            />
          )}
        </div>

        {/* Underlying Price Chart */}
        {priceHistory.length > 0 && (
          <div className="border-t border-white/10 flex flex-col min-h-0">
            {/* Underlying Price Heading */}
            <div className="px-4 py-3 flex-shrink-0">
              <div>
                <div className="text-white/40 text-xs mb-1">
                  {selectedOption ? `${selectedOption.asset} Price` : 'Spot Price'}
                </div>
                <div
                  className={`text-lg font-bold ${
                    underlyingPriceDirection === 'up'
                      ? 'text-price-up'
                      : 'text-price-down'
                  }`}
                >
                  {formatPrice(displayUnderlyingPrice)}
                </div>
              </div>
            </div>
            <div className="h-[150px] min-h-[150px] max-h-[200px] flex-shrink-0 relative overflow-hidden">
              <OptionPriceChart
                priceHistory={priceHistory
                  .filter(p => p.spotPrice != null && !isNaN(p.spotPrice))
                  .map(p => ({
                    ...p,
                    price: p.spotPrice,
                  }))}
                priceDirection={underlyingPriceDirection}
                onHover={setHoveredTimestamp}
                hoveredTimestamp={hoveredTimestamp}
                zoomLevel={zoomLevel}
                panOffset={panOffset}
                onZoomChange={setZoomLevel}
                onPanChange={setPanOffset}
              />
            </div>
          </div>
        )}
      </div>

      {/* User Position Info */}
      {userOwnsOption && (
        <div className="px-4 py-2 bg-blue-500/10 border-t border-blue-500/30">
          <div className="text-blue-400 text-sm">
            You own {userQuantity} contract{userQuantity !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 p-4 border-t border-white/10">
        <motion.button
          className="flex-1 py-3 rounded-lg font-semibold text-sm transition-colors"
          style={{
            backgroundColor: `${brandColors.green}20`,
            borderColor: brandColors.green,
            borderWidth: 1,
            color: brandColors.green,
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowBuyPanel(true)}
        >
          Buy
        </motion.button>

          <motion.button
            className="flex-1 py-3 rounded-lg font-semibold text-sm transition-colors"
          disabled={!userOwnsOption}
            style={{
            backgroundColor: userOwnsOption ? `${brandColors.red}20` : `${brandColors.red}10`,
              borderColor: brandColors.red,
              borderWidth: 1,
            color: userOwnsOption ? brandColors.red : `${brandColors.red}60`,
            opacity: userOwnsOption ? 1 : 0.5,
            cursor: userOwnsOption ? 'pointer' : 'not-allowed',
            }}
          whileHover={userOwnsOption ? { scale: 1.02 } : {}}
          whileTap={userOwnsOption ? { scale: 0.98 } : {}}
          onClick={() => {
            if (userOwnsOption) {
              setShowSellPanel(true);
            }
          }}
          >
            Sell
          </motion.button>
      </div>

      {/* Backdrop overlay - closes panel when clicked */}
      <AnimatePresence>
        {(showBuyPanel || showSellPanel) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/20 z-20"
            onClick={() => {
              setShowBuyPanel(false);
              setShowSellPanel(false);
            }}
          />
        )}
      </AnimatePresence>

      {/* Buy Panel - Overlays bottom chart */}
      <AnimatePresence>
        {selectedOption && showBuyPanel && (
          <div
            className="absolute bottom-0 left-0 right-0 z-30"
            onClick={e => e.stopPropagation()}
          >
            <TradePanel
              isVisible={showBuyPanel}
              tradeType={selectedOption.type}
              strikePrice={selectedOption.strikePrice}
              spotPrice={spotPrice ?? 0}
              asset={selectedOption.asset}
              volatility={
                priceHistory.length > 0
                  ? priceHistory[priceHistory.length - 1].volatility
                  : undefined
              }
              expiryTimestamp={selectedOption.expiryTimestamp}
              onClose={() => setShowBuyPanel(false)}
              onTrade={handleBuy}
            />
          </div>
        )}
      </AnimatePresence>

      {/* Sell Panel - Overlays bottom chart */}
      <AnimatePresence>
        {selectedOption && userOwnsOption && showSellPanel && (
          <div
            className="absolute bottom-0 left-0 right-0 z-30"
            onClick={e => e.stopPropagation()}
          >
            <TradePanel
              isVisible={showSellPanel}
              tradeType={selectedOption.type}
              strikePrice={selectedOption.strikePrice}
              spotPrice={spotPrice ?? 0}
              asset={selectedOption.asset}
              volatility={
                priceHistory.length > 0
                  ? priceHistory[priceHistory.length - 1].volatility
                  : undefined
              }
              expiryTimestamp={selectedOption.expiryTimestamp}
              side="SELL"
              maxQuantity={userQuantity}
              onClose={() => setShowSellPanel(false)}
              onTrade={handleSell}
            />
          </div>
        )}
      </AnimatePresence>

      {/* Option Selector Modal */}
      <OptionSelectorModal
        isVisible={showOptionSelector}
        currentOption={selectedOption}
        onClose={() => setShowOptionSelector(false)}
        onSelect={selectOption}
      />

      {/* Greeks Modal */}
      <GreeksModal
        isVisible={showGreeksModal}
        option={selectedOption}
        spotPrice={spotPrice}
        optionPrice={currentPrice}
        volatility={
          priceHistory.length > 0
            ? priceHistory[priceHistory.length - 1].volatility
            : undefined
        }
        onClose={() => setShowGreeksModal(false)}
      />
    </div>
  );
}
